import { ProfileIcon, BookingsIcon, OrdersIcon, PartsIcon, WalletIcon, AnalyticsIcon, GOServiceIcon, WarningIcon, SuccessIcon, VehicleIcon, ServicesIcon } from "../../lib/cccIcons"
import useIsMobile from "../../lib/useIsMobile"
import { useAuth } from "../../contexts/AuthContext"
import AdminAIMonitor from "./AdminAIMonitor"
import { useEffect, useState, useRef } from "react"
import { supabase } from "../../lib/supabase"
import toast from "react-hot-toast"

function NetworkCanvas() {
  const canvasRef = useRef(null)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    let animId
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight }
    resize()
    window.addEventListener("resize", resize)
    const dots = Array.from({ length: 40 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 2 + 1,
    }))
    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      dots.forEach(d => {
        d.x += d.vx; d.y += d.vy
        if (d.x < 0 || d.x > canvas.width) d.vx *= -1
        if (d.y < 0 || d.y > canvas.height) d.vy *= -1
        ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2)
        ctx.fillStyle = "rgba(230,130,30,0.6)"; ctx.fill()
      })
      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x
          const dy = dots[i].y - dots[j].y
          const dist = Math.sqrt(dx*dx + dy*dy)
          if (dist < 100) {
            ctx.beginPath(); ctx.moveTo(dots[i].x, dots[i].y); ctx.lineTo(dots[j].x, dots[j].y)
            ctx.strokeStyle = `rgba(230,130,30,${0.15*(1-dist/100)})`; ctx.lineWidth = 0.5; ctx.stroke()
          }
        }
      }
      animId = requestAnimationFrame(draw)
    }
    draw()
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize) }
  }, [])
  return <canvas ref={canvasRef} style={{ position:"absolute", inset:0, width:"100%", height:"100%", opacity:0.4 }}/>
}

export default function AdminDashboard() {
  const { user, profile } = useAuth()
  const isMobile = useIsMobile()
  const [stats, setStats] = useState({ users:0, providers:0, drivers:0, customers:0, bookings:0, revenue:0, pending:0, completed:0 })
  const [onlineDrivers, setOnlineDrivers] = useState(0)
  const [sosAlerts, setSosAlerts] = useState([])
  const [activity, setActivity] = useState([])
  const [bookingTrend, setBookingTrend] = useState([])
  const [revenueTrend, setRevenueTrend] = useState([])
  const [topServices, setTopServices] = useState([])
  const [topProviders, setTopProviders] = useState([])
  const [userGrowth, setUserGrowth] = useState([])
  const [loading, setLoading] = useState(true)
  const [time, setTime] = useState(new Date())
  if (!user || profile?.role !== "admin") return null

  useEffect(() => {
    load()
    const tick = setInterval(() => setTime(new Date()), 1000)
    const sub = supabase.channel("admin-live")
      .on("postgres_changes", { event:"*", schema:"public", table:"bookings" }, () => load())
      .on("postgres_changes", { event:"*", schema:"public", table:"profiles" }, () => load())
      .subscribe()
    return () => { supabase.removeChannel(sub); clearInterval(tick) }
  }, [])

  async function loadOnlineDrivers() {
    const { count } = await supabase.from("driver_status").select("*", { count:"exact", head:true }).eq("is_online", true)
    setOnlineDrivers(count||0)
  }

  async function load() {
    const [
      { data: profiles },
      { data: emergencyAlerts },
      { data: bookings },
      { data: driverStatus },
      { data: recentBookings },
      { data: recentUsers },
      { data: recentOrders },
      { data: inventoryItems },
      { data: commissionRates },
    ] = await Promise.all([
      supabase.from("profiles").select("role,created_at,is_active"),
      supabase.from("emergency_alerts").select("*").eq("status","active").order("created_at",{ascending:false}),
      supabase.from("bookings").select("status,total_amount,platform_commission,created_at,booking_date,service_name,customer_id,provider_id").limit(5000),
      supabase.from("driver_status").select("is_online").eq("is_online", true),
      supabase.from("bookings").select("id,service_name,status,total_amount,created_at").order("created_at",{ascending:false}).limit(8),
      supabase.from("profiles").select("id,first_name,last_name,role,created_at").order("created_at",{ascending:false}).limit(5),
      supabase.from("orders").select("id,status,subtotal,created_at").order("created_at",{ascending:false}).limit(5),
      supabase.from("inventory").select("id,is_active,stock_quantity").eq("is_active",true),
      supabase.from("commission_rates").select("provider_type,platform_rate"),
    ])

    const ps = profiles||[]
    const bks = bookings||[]
    const completed = bks.filter(b=>b.status==="completed")

    if (emergencyAlerts && emergencyAlerts.length > 0) {
      const userIds = emergencyAlerts.map(a=>a.user_id)
      const { data: sensitiveData } = await supabase.from("profile_sensitive").select("id,phone").in("id", userIds)
      const phoneMap = {}
      ;(sensitiveData||[]).forEach(s => { phoneMap[s.id] = s.phone })
      setSosAlerts(emergencyAlerts.map(a => ({...a, contact_phone: phoneMap[a.user_id]})))
    } else {
      setSosAlerts([])
    }
    setStats({
      users: ps.length,
      customers: ps.filter(p=>p.role==="customer").length,
      providers: ps.filter(p=>p.role==="provider").length,
      drivers: ps.filter(p=>p.role==="driver").length,
      bookings: bks.length,
      revenue: bks.reduce((s,b)=>s+Number(b.platform_commission||0),0),
      total_revenue: bks.reduce((s,b)=>s+Number(b.total_amount||0),0),
      completed: completed.length,
    })
    setOnlineDrivers(driverStatus?.length||0)
    const pendingOrders = (recentOrders||[]).filter(o=>o.status==="pending").length
    const lowStock = (inventoryItems||[]).filter(i=>i.stock_quantity<=5).length
    setStats(s=>({...s, pendingOrders, lowStock, inventoryItems:(inventoryItems||[]).length }))

    const feed = [
      ...(recentBookings||[]).map(b=>({ type:"booking", text:`Booking: ${b.service_name}`, sub:b.status, time:b.created_at, tag:b.status==="completed"?"Completed":b.status==="cancelled"?"Cancelled":"Pending", icon:"bookings" })),
      ...(recentUsers||[]).map(u=>({ type:"user", text:`New ${u.role}: ${u.first_name} ${u.last_name}`, sub:"registered", time:u.created_at, tag:u.role, icon:"profile" })),
    ].sort((a,b)=>new Date(b.time)-new Date(a.time)).slice(0,10)
    setActivity(feed)

    const byMonth = bks.reduce((acc,b) => {
      const m = b.created_at?.slice(0,7)
      if (!m) return acc
      if (!acc[m]) acc[m] = { month:m, total:0, completed:0, cancelled:0, pending:0 }
      acc[m].total++
      if (b.status==="completed") acc[m].completed++
      else if (b.status==="cancelled") acc[m].cancelled++
      else acc[m].pending++
      return acc
    }, {})
    setBookingTrend(Object.values(byMonth).sort((a,b)=>a.month.localeCompare(b.month)).slice(-6))
    // Revenue trend
    const byRevMonth = bks.filter(b=>b.status==="completed").reduce((acc,b) => {
      const m = b.created_at?.slice(0,7)
      if (!m) return acc
      if (!acc[m]) acc[m] = { month:m, revenue:0, commission:0 }
      acc[m].revenue += Number(b.total_amount||0)
      acc[m].commission += Number(b.platform_commission||0)
      return acc
    }, {})
    setRevenueTrend(Object.values(byRevMonth).sort((a,b)=>a.month.localeCompare(b.month)).slice(-6))
    // Top services
    const bySvc = bks.reduce((acc,b) => {
      if(!b.service_name) return acc
      if(!acc[b.service_name]) acc[b.service_name] = { name:b.service_name, count:0, revenue:0 }
      acc[b.service_name].count++
      acc[b.service_name].revenue += Number(b.total_amount||0)
      return acc
    }, {})
    setTopServices(Object.values(bySvc).sort((a,b)=>b.count-a.count).slice(0,5))

    const byUserMonth = ps.reduce((acc,p) => {
      const m = p.created_at?.slice(0,7)
      if (!m) return acc
      if (!acc[m]) acc[m] = { month:m, total:0, customers:0, providers:0, drivers:0 }
      acc[m].total++
      if (p.role==="customer") acc[m].customers++
      else if (p.role==="provider") acc[m].providers++
      else if (p.role==="driver") acc[m].drivers++
      return acc
    }, {})
    setUserGrowth(Object.values(byUserMonth).sort((a,b)=>a.month.localeCompare(b.month)).slice(-6))
    setLoading(false)
  }

  const maxBookings = Math.max(...bookingTrend.map(m=>m.total), 1)
  const maxUsers = Math.max(...userGrowth.map(m=>m.total), 1)
  function shortMonth(m) { return new Date(m+"-01").toLocaleString("default",{month:"short",year:"2-digit"}) }

  return (
    <div>
      {sosAlerts.length>0&&(
        <div style={{ background:"#fff5f5", border:"2px solid #e24b4a", borderRadius:12, padding:"1rem", marginBottom:"1.25rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:15, fontWeight:800, color:"#e24b4a", marginBottom:8 }}>
            <GOServiceIcon size={14} color="#e24b4a"/> {sosAlerts.length} Active Emergency Alert{sosAlerts.length>1?"s":""}
          </div>
          {sosAlerts.map(a=>(
            <div key={a.id} style={{ background:"#ffffff", borderRadius:8, padding:"0.75rem", marginBottom:6, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:"#000" }}>{a.user_name} ({a.user_role})</div>
                <div style={{ fontSize:11, color:"#888" }}>{new Date(a.created_at).toLocaleString()}</div>
              </div>
              <div style={{ display:"flex", gap:6 }}>
                {a.contact_phone&&(
                  <a href={`tel:${a.contact_phone}`}
                    style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:7, color:"#1d9e75", fontSize:11, padding:"6px 10px", textDecoration:"none", fontWeight:700 }}>
                    📞 Call now
                  </a>
                )}
                {a.latitude&&(
                  <a href={`https://maps.google.com/?q=${a.latitude},${a.longitude}`} target="_blank" rel="noreferrer"
                    style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:7, color:"#378add", fontSize:11, padding:"6px 10px", textDecoration:"none" }}>
                    📍 View location
                  </a>
                )}
                <button onClick={async()=>{
                  await supabase.from("emergency_alerts").update({status:"resolved",resolved_at:new Date().toISOString()}).eq("id",a.id)
                  await supabase.from("notifications").insert({
                    user_id: a.user_id,
                    title: "Help is on the way ✅",
                    message: "An admin has seen your emergency alert and is responding. Stay safe.",
                    type: "info"
                  })
                  load()
                }}
                  style={{ background:"#f0fdf4", border:"1px solid #1d9e7540", borderRadius:7, color:"#1d9e75", fontSize:11, padding:"6px 10px", cursor:"pointer" }}>
                  ✓ Resolve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* QUICK ACTIONS */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:8, marginBottom:"1.25rem" }}>
        {[
          { icon:"users", label:"Users", path:"/admin-dashboard/users", color:"#378add", value:stats.users },
          { icon:"bookings", label:"Bookings", path:"/admin-dashboard/bookings", color:"#e6821e", value:stats.bookings },
          { icon:"orders", label:"Orders", path:"/admin-dashboard/orders", color:"#8b5cf6", value:stats.pendingOrders||0 },
          { icon:"parts", label:"Inventory", path:"/admin-dashboard/inventory", color:"#1d9e75", value:stats.inventoryItems||0 },
          { icon:"wallet", label:"Revenue", path:"/admin-dashboard/revenue", color:"#e6821e", value:"KES "+(stats.revenue||0).toLocaleString() },
          { icon:"diagnostics", label:"Diagnostics", path:"/admin-dashboard/diagnostics", color:"#e24b4a", value:"Check" },
        ].map(a=>(
          <a key={a.path} href={a.path} style={{ background:"#f8f8f8", border:`1px solid ${a.color}30`, borderRadius:10, padding:"0.75rem", textDecoration:"none", textAlign:"center", display:"block" }}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:4}}>{a.icon==="users"?<ProfileIcon size={22} color={a.color}/>:a.icon==="bookings"?<BookingsIcon size={22} color={a.color}/>:a.icon==="orders"?<OrdersIcon size={22} color={a.color}/>:a.icon==="parts"?<PartsIcon size={22} color={a.color}/>:a.icon==="wallet"?<WalletIcon size={22} color={a.color}/>:<AnalyticsIcon size={22} color={a.color}/>}</div>
            <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:800, color:a.color }}>{a.value}</div>
            <div style={{ fontSize:10, color:"#888", marginTop:2 }}>{a.label}</div>
          </a>
        ))}
      </div>

      {/* Hero Banner */}
      <div style={{ position:"relative", background:"#ffffff", border:"1px solid #eeeeee", borderRadius:isMobile?10:16, overflow:"hidden", marginBottom:"1rem", boxShadow:"0 2px 12px rgba(0,0,0,0.04)" }}>
        <NetworkCanvas />
        <div style={{ position:"relative", zIndex:1, padding:isMobile?"1rem":"2rem 1.5rem", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
              <div style={{ width:7, height:7, borderRadius:"50%", background:"#1d9e75", boxShadow:"0 0 8px #1d9e75" }}/>
              <span style={{ fontSize:10, color:"#1d9e75", fontWeight:600, letterSpacing:"0.08em", textTransform:"uppercase" }}>Platform live</span>
            </div>
            <div style={{ fontFamily:"Syne", fontSize:isMobile?18:26, fontWeight:800, color:"#000000", marginBottom:2 }}>
              Car<span style={{ color:"#e6821e" }}>Care</span> Connect
            </div>
            <div style={{ fontSize:11, color:"#888" }}>Admin Control Center · Nairobi</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontFamily:"Syne", fontSize:isMobile?20:28, fontWeight:800, color:"#e6821e", letterSpacing:2 }}>
              {time.toLocaleTimeString([],{hour:"2-digit",minute:"2-digit",second:"2-digit"})}
            </div>
            <div style={{ fontSize:10, color:"#888", marginTop:2 }}>
              {time.toLocaleDateString("default",{weekday:"short",day:"numeric",month:"short"})}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:4, justifyContent:"flex-end", marginTop:4 }}>
              <div style={{ width:5, height:5, borderRadius:"50%", background:onlineDrivers>0?"#1d9e75":"#888" }}/>
              <span style={{ fontSize:10, color:onlineDrivers>0?"#1d9e75":"#888" }}>{onlineDrivers} driver{onlineDrivers!==1?"s":""} online</span>
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div style={{ position:"relative", zIndex:1, display:"grid", gridTemplateColumns:"repeat(2,1fr)", borderTop:"1px solid #eeeeee" }}>
          {[
            { label:"CCC Commission", value:`KES ${Number(stats.revenue).toLocaleString()}`, color:"#e6821e" },
            { label:"Gross revenue", value:`KES ${Number(stats.total_revenue||0).toLocaleString()}`, color:"#1d9e75" },
            { label:"Pending bookings", value:stats.pending, color:stats.pending>0?"#e6821e":"#888" },
            { label:"Total users", value:stats.users, color:"#000000" },
          ].map((s,i)=>(
            <div key={s.label} style={{ padding:isMobile?"0.6rem 0.9rem":"0.9rem 1.25rem", borderRight:i%2===0?"1px solid rgba(255,255,255,0.2)":"none", borderBottom:i<2?"1px solid rgba(255,255,255,0.2)":"none" }}>
              <div style={{ fontSize:9, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:3 }}>{s.label}</div>
              <div style={{ fontFamily:"Syne", fontSize:isMobile?15:18, fontWeight:800, color:s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>
      <AdminAIMonitor />

      {/* Secondary stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, marginBottom:"1rem" }}>
        {[
          { label:"Customers", value:stats.customers, color:"#e6821e" },
          { label:"Providers", value:stats.providers, color:"#378add" },
          { label:"Drivers", value:stats.drivers, color:"#1d9e75" },
          { label:"Completed", value:stats.completed, color:"#1d9e75" },
        ].map(s=>(
          <div key={s.label} style={{ background:"#f8f8f8", borderRadius:10, padding:isMobile?"0.7rem":"1rem", border:"0.5px solid #eee" }}>
            <div style={{ fontSize:9, color:"#888", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:3 }}>{s.label}</div>
            <div style={{ fontFamily:"Syne", fontSize:isMobile?16:20, fontWeight:800, color:s.color||"#ffffff" }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:8, marginBottom:"1rem" }}>
        <div style={{ background:"#f8f8f8", border:"0.5px solid #eee", borderRadius:12, padding:"1rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:2, color:"#000000" }}>Booking trend</div>
          <div style={{ fontSize:10, color:"#888", marginBottom:"0.75rem" }}>Per month</div>
          {bookingTrend.length===0&&<div style={{ color:"#aaa", fontSize:12 }}>No data yet</div>}
          <div style={{ display:"flex", gap:6, alignItems:"flex-end" }}>
            {bookingTrend.map(m=>(
              <div key={m.month} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                <div style={{ fontSize:8, color:"#888", fontWeight:600 }}>{m.total}</div>
                <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:1 }}>
                  <div style={{ width:"100%", background:"#1d9e75", borderRadius:"2px 2px 0 0", height:`${Math.max(2,(m.completed/maxBookings)*50)}px` }}/>
                  <div style={{ width:"100%", background:"#e6821e", height:`${Math.max(2,(m.pending/maxBookings)*50)}px` }}/>
                  <div style={{ width:"100%", background:"#e24b4a", height:`${Math.max(m.cancelled>0?2:0,(m.cancelled/maxBookings)*50)}px` }}/>
                </div>
                <div style={{ fontSize:7, color:"#888", textAlign:"center" }}>{shortMonth(m.month)}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
            {[{label:"Done",bg:"#1d9e75"},{label:"Pending",bg:"#e6821e"},{label:"Cancelled",bg:"#e24b4a"}].map(l=>(
              <div key={l.label} style={{ display:"flex", alignItems:"center", gap:3 }}>
                <div style={{ width:8, height:8, background:l.bg, borderRadius:2 }}/>
                <span style={{ fontSize:9, color:"#888" }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background:"#f8f8f8", border:"0.5px solid #eee", borderRadius:12, padding:"1rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:2, color:"#000000" }}>User growth</div>
          <div style={{ fontSize:10, color:"#888", marginBottom:"0.75rem" }}>Per month</div>
          {userGrowth.length===0&&<div style={{ color:"#aaa", fontSize:12 }}>No data yet</div>}
          <div style={{ display:"flex", gap:6, alignItems:"flex-end" }}>
            {userGrowth.map(m=>(
              <div key={m.month} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                <div style={{ fontSize:8, color:"#888", fontWeight:600 }}>{m.total}</div>
                <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:1 }}>
                  <div style={{ width:"100%", background:"#1d9e75", borderRadius:"2px 2px 0 0", height:`${Math.max(2,(m.customers/maxUsers)*50)}px` }}/>
                  <div style={{ width:"100%", background:"#378add", height:`${Math.max(2,(m.providers/maxUsers)*50)}px` }}/>
                  <div style={{ width:"100%", background:"#1d9e75", height:`${Math.max(m.drivers>0?2:0,(m.drivers/maxUsers)*50)}px` }}/>
                </div>
                <div style={{ fontSize:7, color:"#888", textAlign:"center" }}>{shortMonth(m.month)}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
            {[{label:"Customers",bg:"#e6821e"},{label:"Providers",bg:"#378add"},{label:"Drivers",bg:"#1d9e75"}].map(l=>(
              <div key={l.label} style={{ display:"flex", alignItems:"center", gap:3 }}>
                <div style={{ width:8, height:8, background:l.bg, borderRadius:2 }}/>
                <span style={{ fontSize:9, color:"#888" }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue Trend */}
      <div style={{ background:"#f8f8f8", border:"0.5px solid #eee", borderRadius:12, padding:"1rem", marginBottom:"1rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:2, color:"#000000" }}>Revenue trend</div>
        <div style={{ fontSize:10, color:"#888", marginBottom:"0.75rem" }}>Platform commission per month</div>
        {revenueTrend.length===0&&<div style={{ color:"#aaa", fontSize:12 }}>No data yet</div>}
        <div style={{ display:"flex", gap:6, alignItems:"flex-end" }}>
          {revenueTrend.map(m=>{
            const maxRev = Math.max(...revenueTrend.map(r=>r.revenue), 1)
            return (
              <div key={m.month} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2 }}>
                <div style={{ fontSize:8, color:"#888", fontWeight:600 }}>KES {(m.commission/1000).toFixed(0)}k</div>
                <div style={{ width:"100%", background:"#e6821e", borderRadius:"3px 3px 0 0", height:`${Math.max(4,(m.revenue/maxRev)*60)}px` }}/>
                <div style={{ fontSize:7, color:"#888", textAlign:"center" }}>{shortMonth(m.month)}</div>
              </div>
            )
          })}
        </div>
        <div style={{ display:"flex", gap:12, marginTop:8 }}>
          <div style={{ fontSize:11, color:"#888" }}>Total revenue: <span style={{ color:"#e6821e", fontWeight:700 }}>KES {revenueTrend.reduce((s,m)=>s+m.revenue,0).toLocaleString()}</span></div>
          <div style={{ fontSize:11, color:"#888" }}>CCC earned: <span style={{ color:"#1d9e75", fontWeight:700 }}>KES {revenueTrend.reduce((s,m)=>s+m.commission,0).toLocaleString()}</span></div>
        </div>
      </div>
      {/* Top Services */}
      {topServices.length>0&&(
        <div style={{ background:"#f8f8f8", border:"0.5px solid #eee", borderRadius:12, padding:"1rem", marginBottom:"1rem" }}>
          <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:"0.75rem", color:"#000000" }}>Top services</div>
          {topServices.map((s,i)=>(
            <div key={s.name} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0", borderBottom:"1px solid #f0f0f0" }}>
              <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:800, color:"#e6821e", width:20 }}>#{i+1}</div>
              <div style={{ flex:1, fontSize:12, color:"#000", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.name}</div>
              <div style={{ fontSize:11, color:"#888" }}>{s.count} bookings</div>
              <div style={{ fontSize:11, color:"#1d9e75", fontWeight:700 }}>KES {s.revenue.toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
      {/* Activity feed */}
      <div style={{ background:"#f8f8f8", border:"0.5px solid #eee", borderRadius:12, padding:"1rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:"0.75rem", color:"#000000", display:"flex", alignItems:"center", gap:6 }}>
          <div style={{ width:7, height:7, borderRadius:"50%", background:"#e6821e" }}/>
          Live activity
        </div>
        {loading&&<div style={{ color:"#888", fontSize:12 }}>Loading...</div>}
        {!loading&&activity.length===0&&<div style={{ color:"#aaa", fontSize:12 }}>No activity yet</div>}
        {activity.map((a,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:"1px solid #f5f5f5" }}>
            <div style={{ width:28, height:28, borderRadius:7, background:"#f5f5f5", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              {a.icon==="profile"?<ProfileIcon size={16} color="#64748B"/>:a.icon==="bookings"?<BookingsIcon size={16} color="#e6821e"/>:<ServicesIcon size={16} color="#378add"/>}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:11, color:"#000000", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.text}</div>
              <div style={{ fontSize:9, color:"#888", marginTop:1 }}>{a.sub}</div>
            </div>
            <div style={{ flexShrink:0, textAlign:"right" }}>
              <div style={{ fontSize:9, padding:"2px 6px", borderRadius:8, background:"#f5f5f5", color:"#888", marginBottom:2 }}>{a.tag}</div>
              <div style={{ fontSize:9, color:"#aaa" }}>{new Date(a.time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}









